import { FC, useEffect } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { Drawer, Wrapper, Text } from '@lounge-fe/ui-kit'
import { FilterForm, FiltersDrawerProps } from './types'
import { GridSizeSelector } from '@/pages/Collection/Filters/GridSizeSelector'
import { TagFilterBar } from './TagFilterBar/TagFilterBar'
import { Form } from './Form'
import { useSearch } from '@/features/header/components/Header/useSearch'
import { twMerge } from 'tailwind-merge'
import { useSearchParams } from '@/hooks/useSearchParams'
import classNames from 'classnames'

export const FiltersDrawer: FC<FiltersDrawerProps> = ({
  className,
  drawerTitle,
  defaultValues,
  filterSections,
  isLoading,
  onSubmit,
  showGridSizeSelector,
  sortOptions,
  tags,
  title,
  ...props
}) => {
  const methods = useForm<FilterForm>({
    ...props,
    defaultValues,
  })

  useEffect(() => {
    const subscription = methods.watch(() => methods.handleSubmit(onSubmit)())
    return () => subscription.unsubscribe()
  }, [methods.handleSubmit, methods.watch])

  const { isSearchOpen } = useSearch()

  const { searchParams } = useSearchParams()

  useEffect(() => {
    if (!isSearchOpen) {
      // Due to the fact the header is always rendered for SEO purposes
      // we need to reset the filters drawer form manually when search is closed
      methods.reset()
    }
  }, [isSearchOpen])

  const filterSectionsApplied = [...searchParams.entries()]
    .flatMap(([key]) => key)
    .filter((key) => defaultValues && key in defaultValues).length

  return (
    <FormProvider {...methods}>
      {tags && tags.length > 0 && (
        <TagFilterBar name="tags" tags={tags} isLoading={isLoading} />
      )}
      <Wrapper
        size="fluid"
        className={twMerge(
          'flex flex-row justify-between py-6 border-t border-[#E7E7E7] sticky top-0 bg-primary z-[15]',
          className,
        )}
      >
        <Drawer exclusive>
          {({ onClose }) => (
            <>
              <Drawer.Trigger>
                <button
                  className={classNames(
                    'typography--body-md w-fit',
                    filterSectionsApplied > 0 && 'flex gap-x-1',
                  )}
                >
                  <span className="underline underline-offset-4">{title}</span>
                  {filterSectionsApplied > 0 && (
                    <span className="typography--body-md">
                      {`(${filterSectionsApplied})`}
                    </span>
                  )}
                </button>
              </Drawer.Trigger>
              <Drawer.Content direction="left" className="max-w-[450px]">
                <Drawer.Header className="sm:px-10 sm:py-8">
                  <Text variant="title-five">{drawerTitle}</Text>
                </Drawer.Header>
                <Drawer.Body className="py-4 sm:px-10">
                  <Form
                    name={'filters'}
                    sections={filterSections}
                    showActions={true}
                    onClose={onClose}
                    sortOptions={sortOptions}
                  />
                </Drawer.Body>
              </Drawer.Content>
            </>
          )}
        </Drawer>
        {showGridSizeSelector ? <GridSizeSelector /> : undefined}
      </Wrapper>
    </FormProvider>
  )
}
